/**
 * TurnosAdmin Page
 * Admin interface for managing work shifts (Mañana, Tarde, Noche, etc.)
 */
import { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import { motivos as motivosApi } from '../services/api';
import { Plus, Edit2, Trash2, Check, X, Clock, Loader2, Palette } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const TurnosAdmin = () => {
    const toast = useToast();
    const [turnos, setTurnos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTurno, setEditingTurno] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        color_bg: '#3b82f6',
        color_bg_secondary: null,
        color_text: '#ffffff',
        activo: true
    });
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, turno: null });

    // Predefined colors for quick selection
    const presetColors = [
        '#ef4444', // red
        '#f97316', // orange
        '#eab308', // yellow
        '#22c55e', // green
        '#14b8a6', // teal
        '#3b82f6', // blue
        '#1e40af', // deep blue
        '#6366f1', // indigo
        '#8b5cf6', // violet
        '#ec4899', // pink
        '#6b7280', // gray
        '#000000', // black
        '#ffffff', // white
    ];

    useEffect(() => {
        loadTurnos();
    }, []);

    const loadTurnos = async () => {
        try {
            setLoading(true);
            const data = await motivosApi.getTurnos();
            setTurnos(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error('Error al cargar turnos');
            console.error(error);
            setTurnos([]);
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditingTurno(null);
        setFormData({ nombre: '', color_bg: '#3b82f6', color_bg_secondary: null, color_text: '#ffffff', activo: true });
        setShowModal(true);
    };

    const openEditModal = (turno) => {
        setEditingTurno(turno);
        setFormData({
            nombre: turno.nombre,
            color_bg: turno.color_bg,
            color_bg_secondary: turno.color_bg_secondary || null,
            color_text: turno.color_text || '#ffffff',
            activo: turno.activo === 1 || turno.activo === true
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingTurno(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        try {
            setSaving(true);
            if (editingTurno) {
                await motivosApi.actualizarTurno(editingTurno.id, formData);
                toast.success('Turno actualizado');
            } else {
                await motivosApi.crearTurno(formData);
                toast.success('Turno creado');
            }
            closeModal();
            loadTurnos();
        } catch (error) {
            toast.error(error.message || 'Error al guardar turno');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (turno) => {
        try {
            const newStatus = !(turno.activo === 1 || turno.activo === true);
            await motivosApi.actualizarTurno(turno.id, { ...turno, activo: newStatus });
            toast.success(newStatus ? 'Turno activado' : 'Turno desactivado');
            loadTurnos();
        } catch (error) {
            toast.error('Error al cambiar estado');
        }
    };

    const handleDelete = async (turno) => {
        setDeleteConfirm({ isOpen: true, turno });
    };

    const confirmDelete = async () => {
        const turno = deleteConfirm.turno;
        if (!turno) return;

        try {
            await motivosApi.eliminarTurno(turno.id);
            toast.success('Turno eliminado');
            loadTurnos();
        } catch (error) {
            toast.error(error.message || 'Error al eliminar turno');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Clock className="w-7 h-7 text-cyan-400" />
                        Administración de Turnos
                    </h1>
                    <p className="text-gray-400 mt-1">
                        Configura los tipos de jornada y sus colores visuales
                    </p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Nuevo Turno
                </button>
            </div>

            {/* Turnos List */}
            <div className="card">
                {turnos.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No hay turnos configurados</p>
                        <button onClick={openCreateModal} className="text-cyan-400 mt-2 hover:underline">
                            Crea el primero para empezar
                        </button>
                    </div>
                ) : (
                    <div className="divide-y divide-white/10">
                        {turnos.map((turno) => (
                            <div
                                key={turno.id}
                                className={`flex items-center justify-between p-4 ${!(turno.activo === 1 || turno.activo === true) ? 'opacity-50' : ''}`}
                            >
                                <div className="flex items-center gap-4">
                                    {/* Preview of the shift as it would look on the calendar */}
                                    <div
                                        className="w-12 h-12 rounded-xl flex items-center justify-center font-bold shadow-lg overflow-hidden"
                                        style={turno.color_bg_secondary ? {
                                            background: `linear-gradient(135deg, ${turno.color_bg} 50%, ${turno.color_bg_secondary} 50%)`,
                                            color: turno.color_text || '#ffffff'
                                        } : {
                                            backgroundColor: turno.color_bg,
                                            color: turno.color_text || '#ffffff'
                                        }}
                                    >
                                        {turno.nombre.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-white flex items-center gap-2">
                                            {turno.nombre}
                                            {!(turno.activo === 1 || turno.activo === true) && (
                                                <span className="text-xs bg-gray-600 px-2 py-0.5 rounded-full">
                                                    Inactivo
                                                </span>
                                            )}
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Fondo: {turno.color_bg} | Texto: {turno.color_text || '#ffffff'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    {/* Toggle active */}
                                    <button
                                        onClick={() => handleToggleActive(turno)}
                                        className={`p-2 rounded-lg transition-colors ${(turno.activo === 1 || turno.activo === true)
                                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                            : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                                            }`}
                                        title={(turno.activo === 1 || turno.activo === true) ? 'Desactivar' : 'Activar'}
                                    >
                                        {(turno.activo === 1 || turno.activo === true) ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                    </button>

                                    {/* Edit */}
                                    <button
                                        onClick={() => openEditModal(turno)}
                                        className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
                                        title="Editar"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>

                                    {/* Delete */}
                                    <button
                                        onClick={() => handleDelete(turno)}
                                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                        title="Eliminar"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/70">
                    <div className="bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-cyan-500/20 w-full max-w-md">
                        <div className="p-6 border-b border-white/10">
                            <h2 className="text-xl font-bold text-white">
                                {editingTurno ? 'Editar Turno' : 'Nuevo Turno'}
                            </h2>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {/* Nombre */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Nombre *
                                </label>
                                <input
                                    type="text"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Ej: Mañana"
                                    className="input-field"
                                    autoFocus
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Color Fondo */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Color Fondo
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={formData.color_bg}
                                            onChange={(e) => setFormData({ ...formData, color_bg: e.target.value })}
                                            className="w-10 h-10 rounded bg-transparent border-none cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={formData.color_bg}
                                            onChange={(e) => setFormData({ ...formData, color_bg: e.target.value })}
                                            className="input-field text-xs px-2"
                                        />
                                    </div>
                                </div>

                                {/* Color Texto */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Color Texto
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={formData.color_text}
                                            onChange={(e) => setFormData({ ...formData, color_text: e.target.value })}
                                            className="w-10 h-10 rounded bg-transparent border-none cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={formData.color_text}
                                            onChange={(e) => setFormData({ ...formData, color_text: e.target.value })}
                                            className="input-field text-xs px-2"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Bicolor toggle */}
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="bicolor"
                                    checked={!!formData.color_bg_secondary}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        color_bg_secondary: e.target.checked ? '#22c55e' : null
                                    })}
                                    className="w-4 h-4 rounded border-white/10 bg-white/5 text-cyan-400 focus:ring-cyan-400"
                                />
                                <label htmlFor="bicolor" className="text-sm text-gray-300">Turno bicolor (diagonal)</label>
                            </div>

                            {/* Secondary color - only show if bicolor is enabled */}
                            {formData.color_bg_secondary && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Color Secundario (diagonal)
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={formData.color_bg_secondary}
                                            onChange={(e) => setFormData({ ...formData, color_bg_secondary: e.target.value })}
                                            className="w-10 h-10 rounded bg-transparent border-none cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={formData.color_bg_secondary}
                                            onChange={(e) => setFormData({ ...formData, color_bg_secondary: e.target.value })}
                                            className="input-field text-xs px-2"
                                        />
                                    </div>
                                    {/* Quick colors for secondary */}
                                    <div className="flex gap-2 flex-wrap mt-2">
                                        {presetColors.slice(0, 8).map((color) => (
                                            <button
                                                key={color}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, color_bg_secondary: color })}
                                                className={`w-6 h-6 rounded-lg transition-transform hover:scale-110 ${formData.color_bg_secondary === color ? 'ring-2 ring-white ring-offset-1 ring-offset-gray-900' : ''}`}
                                                style={{ backgroundColor: color }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Preview */}
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">Vista previa en calendario</label>
                                <div className="flex justify-center p-4 bg-white/5 rounded-xl border border-dashed border-white/10">
                                    <div
                                        className="w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-xl shadow-xl overflow-hidden"
                                        style={formData.color_bg_secondary ? {
                                            background: `linear-gradient(135deg, ${formData.color_bg} 50%, ${formData.color_bg_secondary} 50%)`,
                                            color: formData.color_text
                                        } : {
                                            backgroundColor: formData.color_bg,
                                            color: formData.color_text
                                        }}
                                    >
                                        {formData.nombre ? formData.nombre.charAt(0).toUpperCase() : '?'}
                                    </div>
                                </div>
                            </div>

                            {/* Preset colors */}
                            <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                                    <Palette className="w-3.5 h-3.5" /> Colores rápidos
                                </label>
                                <div className="flex gap-2 flex-wrap">
                                    {presetColors.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, color_bg: color })}
                                            className={`w-7 h-7 rounded-lg transition-transform hover:scale-110 ${formData.color_bg === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <input
                                    type="checkbox"
                                    id="activo"
                                    checked={formData.activo}
                                    onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                                    className="w-4 h-4 rounded border-white/10 bg-white/5 text-cyan-400 focus:ring-cyan-400"
                                />
                                <label htmlFor="activo" className="text-sm text-gray-300">Turno activo</label>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn-secondary flex-1"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {saving ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Check className="w-4 h-4" />
                                    )}
                                    {editingTurno ? 'Guardar Cambios' : 'Crear Turno'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, turno: null })}
                onConfirm={confirmDelete}
                title="Eliminar Turno"
                message={`¿Estás seguro de que deseas eliminar el turno "${deleteConfirm.turno?.nombre}"? Esta acción no se puede deshacer si el turno ya ha sido utilizado.`}
                confirmText="Eliminar Permanentemente"
                variant="danger"
            />
        </div>
    );
};

export default TurnosAdmin;
